import { useEffect, useState, useCallback } from "react";
import { Plus, Pencil, Trash2, Bus, Check, X, ChevronDown, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Command, CommandInput, CommandList, CommandEmpty, CommandItem } from "@/components/ui/command";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

export default function CorporateBuses() {
  return (
    <main className="px-6 md:px-10 py-8" data-testid="corporate-buses-page">
      <DistrictBuses />
    </main>
  );
}

function DistrictBuses() {
  const [buses, setBuses] = useState([]);
  const [options, setOptions] = useState({ schools: [], drivers: [] });
  const [loading, setLoading] = useState(true);
  const [dlg, setDlg] = useState(null); // null | {} (new) | bus
  const [form, setForm] = useState({ number: "", company_id: "", driver_id: "", school_ids: [] });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [b, o] = await Promise.all([api.get("/corporate/buses"), api.get("/corporate/bus-options")]);
      setBuses(b.data);
      setOptions(o.data);
    } catch { /* not fatal */ } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (!loading && options.schools.length === 0) return null;

  const openDlg = (bus) => {
    setForm(bus
      ? { number: bus.number, company_id: bus.home_school_id, driver_id: bus.driver_id || "", school_ids: bus.school_ids || [] }
      : { number: "", company_id: options.schools[0]?.id || "", driver_id: "", school_ids: [] });
    setDlg(bus || {});
  };

  const toggleSchool = (sid) => setForm((f) => ({
    ...f,
    school_ids: f.school_ids.includes(sid) ? f.school_ids.filter((x) => x !== sid) : [...f.school_ids, sid],
  }));

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (dlg?.id) await api.put(`/corporate/buses/${dlg.id}`, form);
      else await api.post("/corporate/buses", form);
      toast.success(dlg?.id ? "Bus updated" : `Bus ${form.number} added`);
      setDlg(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (bus) => {
    if (!window.confirm(`Delete bus ${bus.number} (${bus.home_school_name})? Riders will be unassigned.`)) return;
    try {
      await api.delete(`/corporate/buses/${bus.id}`);
      setBuses((bs) => bs.filter((b) => b.id !== bus.id));
      toast.success("Bus deleted");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Delete failed");
    }
  };

  return (
    <section data-testid="corporate-buses-section">
      <div className="mb-4 flex items-end justify-between gap-4">
        <div>
          <h2 className="font-display text-xl font-bold tracking-tight text-slate-900">District buses</h2>
          <p className="text-sm text-slate-600">Add buses once and assign them to any school in your district — shared buses appear on every assigned school's dismissal console.</p>
        </div>
        <button onClick={() => openDlg(null)} data-testid="corporate-add-bus-btn"
          className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-white hover:shadow transition shrink-0">
          <Plus className="h-4 w-4" /> Add bus
        </button>
      </div>
      <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="corporate-buses-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-slate-500 bg-slate-50">
                <th className="px-6 py-3">Bus</th>
                <th className="px-6 py-3">Home school</th>
                <th className="px-6 py-3">Driver</th>
                <th className="px-6 py-3">Also serves</th>
                <th className="px-6 py-3">Riders</th>
                <th className="px-6 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={6} className="px-6 py-10 text-center text-slate-500">Loading…</td></tr>}
              {!loading && buses.length === 0 && (
                <tr><td colSpan={6} className="px-6 py-10 text-center text-slate-500">No buses in the district yet — click "Add bus".</td></tr>
              )}
              {buses.map((b) => (
                <tr key={b.id} className="border-t border-slate-100 hover:bg-slate-50/60" data-testid={`corporate-bus-row-${b.id}`}>
                  <td className="px-6 py-3.5">
                    <span className="inline-flex items-center gap-2 font-semibold text-slate-900">
                      <span className="h-8 w-8 rounded-lg bg-amber-100 text-amber-700 inline-flex items-center justify-center"><Bus className="h-4 w-4" /></span>
                      {b.number}
                    </span>
                  </td>
                  <td className="px-6 py-3.5 text-slate-700">{b.home_school_name}</td>
                  <td className="px-6 py-3.5 text-slate-700">{b.driver_name || <span className="text-slate-400">Unassigned</span>}</td>
                  <td className="px-6 py-3.5">
                    {(b.shared_with || []).length === 0
                      ? <span className="text-slate-400">—</span>
                      : <div className="flex flex-wrap gap-1">
                          {b.shared_with.map((n) => (
                            <span key={n} className="inline-flex rounded-full bg-blue-50 px-2 py-0.5 text-[11px] font-medium text-blue-700">{n}</span>
                          ))}
                        </div>}
                  </td>
                  <td className="px-6 py-3.5 text-slate-700">{b.rider_count}</td>
                  <td className="px-6 py-3.5">
                    <div className="flex items-center justify-end gap-2">
                      <button onClick={() => openDlg(b)} data-testid={`corporate-bus-edit-${b.id}`}
                        className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 transition">
                        <Pencil className="h-3.5 w-3.5" /> Edit
                      </button>
                      <button onClick={() => remove(b)} data-testid={`corporate-bus-delete-${b.id}`}
                        className="inline-flex items-center justify-center rounded-md border border-rose-200 bg-white h-7 w-7 text-rose-600 hover:bg-rose-50 transition">
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent className="sm:max-w-md" data-testid="corporate-bus-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <Bus className="h-5 w-5 text-amber-600" /> {dlg?.id ? `Edit bus ${dlg.number}` : "Add district bus"}
            </DialogTitle>
            <DialogDescription>Pick the home school, then tick any other schools this bus also serves.</DialogDescription>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4 pt-1">
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="block mb-1 text-xs uppercase tracking-widest text-slate-500">Bus number</span>
                <input required value={form.number} onChange={(e) => setForm({ ...form, number: e.target.value })}
                  data-testid="corporate-bus-number-input"
                  className="w-full h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm focus:border-primary focus:ring-4 focus:ring-primary/15 outline-none" />
              </label>
              <label className="block">
                <span className="block mb-1 text-xs uppercase tracking-widest text-slate-500">Home school</span>
                <select value={form.company_id} onChange={(e) => setForm({ ...form, company_id: e.target.value, school_ids: form.school_ids.filter((s) => s !== e.target.value) })}
                  data-testid="corporate-bus-school-select"
                  className="w-full h-10 rounded-lg border border-slate-200 bg-white px-2.5 text-sm focus:border-primary outline-none">
                  {options.schools.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </label>
            </div>
            <label className="block">
              <span className="block mb-1 text-xs uppercase tracking-widest text-slate-500">Driver</span>
              <select value={form.driver_id} onChange={(e) => setForm({ ...form, driver_id: e.target.value })}
                data-testid="corporate-bus-driver-select"
                className="w-full h-10 rounded-lg border border-slate-200 bg-white px-2.5 text-sm focus:border-primary outline-none">
                <option value="">No driver yet</option>
                {options.drivers.map((d) => <option key={d.id} value={d.id}>{d.name} — {d.school_name}</option>)}
              </select>
            </label>
            {options.schools.length > 1 && (
              <div>
                <span className="block mb-1.5 text-xs uppercase tracking-widest text-slate-500">Also serves</span>
                <Popover>
                  <PopoverTrigger asChild>
                    <button type="button" data-testid="corporate-bus-serves-dropdown"
                      className="w-full h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm text-left flex items-center justify-between gap-2 hover:bg-slate-50 focus:border-primary outline-none transition">
                      <span className={form.school_ids.length ? "text-slate-900" : "text-slate-400"}>
                        {form.school_ids.length
                          ? `${form.school_ids.length} school${form.school_ids.length === 1 ? "" : "s"} selected`
                          : "Select schools this bus also serves…"}
                      </span>
                      <ChevronDown className="h-4 w-4 text-slate-400 shrink-0" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="p-0 w-[--radix-popover-trigger-width]" align="start">
                    <Command>
                      <CommandInput placeholder="Search schools…" data-testid="corporate-bus-serves-search" />
                      <CommandList className="max-h-52">
                        <CommandEmpty>No schools found.</CommandEmpty>
                        {options.schools.filter((s) => s.id !== form.company_id).map((s) => {
                          const on = form.school_ids.includes(s.id);
                          return (
                            <CommandItem key={s.id} value={s.name} onSelect={() => toggleSchool(s.id)}
                              data-testid={`corporate-bus-serve-${s.id}`} className="gap-2 cursor-pointer">
                              <span className={`h-4 w-4 shrink-0 rounded border inline-flex items-center justify-center ${
                                on ? "bg-primary border-primary text-white" : "border-slate-300"
                              }`}>
                                {on && <Check className="h-3 w-3" />}
                              </span>
                              {s.name}
                            </CommandItem>
                          );
                        })}
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
                {form.school_ids.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {form.school_ids.map((sid) => {
                      const s = options.schools.find((x) => x.id === sid);
                      return s ? (
                        <span key={sid} className="inline-flex items-center gap-1 rounded-full bg-blue-50 pl-2.5 pr-1 py-0.5 text-[11px] font-medium text-blue-700">
                          {s.name}
                          <button type="button" onClick={() => toggleSchool(sid)} data-testid={`corporate-bus-unserve-${sid}`}
                            className="h-4 w-4 rounded-full hover:bg-blue-100 inline-flex items-center justify-center">
                            <X className="h-2.5 w-2.5" />
                          </button>
                        </span>
                      ) : null;
                    })}
                  </div>
                )}
              </div>
            )}
            <DialogFooter>
              <button type="submit" disabled={busy || !form.number.trim() || !form.company_id} data-testid="corporate-bus-save"
                className="h-10 rounded-lg bg-primary px-6 text-sm font-medium text-white inline-flex items-center justify-center gap-2 hover:shadow disabled:opacity-50 transition">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} {dlg?.id ? "Save changes" : "Add bus"}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </section>
  );
}
